import { motion } from "framer-motion";
import TeamMemberCard from "./TeamMemberCard";

interface TeamMember {
  name: string;
  role: string;
  image?: string;
}

interface TeamSectionProps {
  id: string;
  title: string;
  icon: string;
  members: TeamMember[];
}

const TeamSection = ({ id, title, icon, members }: TeamSectionProps) => {
  const getGridCols = () => {
    if (members.length <= 2) return "grid-cols-1 sm:grid-cols-2";
    if (members.length <= 3) return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3";
    if (members.length <= 4) return "grid-cols-2 sm:grid-cols-2 md:grid-cols-4";
    return "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5";
  };

  const getTotalInRow = () => {
    if (members.length <= 2) return 2;
    if (members.length <= 3) return 3;
    if (members.length <= 4) return 4;
    return 5;
  };

  return (
    <motion.section
      id={id}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className="py-16 md:py-20 scroll-mt-20"
    >
      {/* Section Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="flex items-center justify-center gap-4 mb-14 md:mb-18"
      >
        {/* Left line with glow */}
        <motion.div 
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-transparent via-primary/30 to-primary/60 origin-right"
        />
        
        {/* Icon and Title */}
        <div className="flex items-center gap-3 px-6 py-3 rounded-full bg-card/50 backdrop-blur-sm border border-primary/20">
          <motion.span 
            className="text-2xl"
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            {icon}
          </motion.span>
          <h2 className="font-display text-lg sm:text-xl md:text-2xl font-bold tracking-[0.15em] text-foreground uppercase">
            {title}
          </h2>
        </div>
        
        {/* Right line with glow */}
        <motion.div 
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="h-px flex-1 max-w-[120px] bg-gradient-to-l from-transparent via-primary/30 to-primary/60 origin-left"
        />
      </motion.div>

      {/* Members Grid - FIXED */}
      <div className={`grid ${getGridCols()} gap-6 md:gap-8  max-w-6xl mx-auto`}>
        {members.map((member, index) => (
          <TeamMemberCard
            key={`${member.name}-${index}`}
            name={member.name}
            role={member.role}
            image={member.image}
            index={index}
            totalInRow={getTotalInRow()}
            categoryIcon={icon}
          />
        ))}
      </div>
    </motion.section>
  );
};

export default TeamSection;